import { useTranslation } from "react-i18next";
import styles from "./Observation.module.scss";
import { Box, Paper, FormControl, FormLabel, TextField, Button, Alert } from "@mui/material";
import MainContext from "../../context/mainContext";
import BackButton from "../../components/BackButton";
import SpinnerArea from "../../components/SpinnerArea";
import observationsService from "../../services/observations.service";
import { useContext, useEffect, useState } from "react";
import { AllObservationData, IdObservationData, ObservationData } from '../../models/observation.model';
import { useNavigate, useParams } from "react-router";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as Yup from "yup";
import { useFormik } from "formik";
import { validateBeforeExecute } from "../../misc/utils.functions";
import { PaginatedDataTable } from "../../components/Table";
import { PagedFilterObservations } from "../../misc/pagedFilter";
import useServiceReducer from "../../hooks/useServiceReducer";
import observationsReducer from "./observationsReducer";
import { GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import StatusObservation from "./StatusObservation";
import { ConfirmDialog } from "../tasks/task/TaskDialogs";
import SelectFilter from "../../components/SelectFilter";
import useService from "../../hooks/useService";
import documentsService from "../../services/document.service";
import DocumentDownloadButton from "../../components/DocumentDownload/DocumentDownloadButton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SmartText from "../../components/SmartText";

const _emptyObservationInfo: ObservationData = {
    ticketNumber: "",
    observation: "",
}


const ObservationsByPublications: React.FC = () => {
    const { ticketNumber } = useParams();
    const { setBanner, service } = useContext(MainContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [newObservation, setNewObservation] = useState<ObservationData | null>(null);
    const [editState, setEditState] = useState<IdObservationData>();
    const [remainingDays, setRemainingDays] = useState<number>(0);
    const [readOnlyRemainingDays, setReadOnlyRemainingDays] = useState(true);

    const initFilter: PagedFilterObservations = {
        pageNumber: 1,
        pageSize: 5,
        sortField: "createdDate",
        sortDescending: false,
        ticketNumber: ticketNumber,
        email: "All",
    };

    const [data, loading, filter, dispatch] = useServiceReducer(
        observationsService.searchObservations,
        observationsReducer,
        initFilter,
    );

    const handlePageChange = (pageNumber: number) => {
        dispatch({ type: "PAGE", value: pageNumber });
    };

    const columns: GridColDef[] = [
        {
            field: "createdDate",
            headerName: t("date")!,
            sortable: false,
            width: 130,
            valueFormatter: (params) => moment(params?.value).format("MM/DD/YYYY"),
        },
        {
            field: "status",
            headerName: t("status")!,
            sortable: false,
            width: 160,
            renderCell: (params) => {
                return <StatusObservation statusCode={params.row.status} action={() => handleState(params.row.id)} disabled={params.row.notCanEditState}  />
            },
        },
        {
            field: "displayName",
            headerName: t("name")!,
            sortable: false,
            align: "left",
            width: 280
        },
        {
            field: "observation",
            headerName: t("observation")!,
            headerAlign: "left",
            sortable: false,
            align: "left",
            flex: 1,
            cellClassName: styles.widthObservation,
            renderCell: (params) => {
                return <SmartText text={params.row.observation} length={400} />
            }
        }
    ];

    const addObservation = (values: ObservationData) => {
        if (remainingDays > 0) {
            service(() => observationsService.addObservation(values, ticketNumber), {
                callback: () => {
                    setNewObservation(null);
                    formik.handleReset(newObservation);
                    handlePageChange(1);
                    reloadUsers();
                },
            });
        }
    };

    const handleState = (id: IdObservationData) => {
        setEditState(id)
    };

    const editStateObservation = () => {
        if (editState) {
            service(() => observationsService.editStateObservation(editState), {
                callback: () => {
                    setEditState(undefined);
                    handlePageChange(1);
                },
            });
        }
    }

    const validationSchema = Yup.object().shape({
        observation: Yup.string().nullable().required(t("requiredField")!).max(2000),
    });

    const formik = useFormik<ObservationData>({
        initialValues: _emptyObservationInfo,
        onSubmit: () => console.log("submit"),
        validationSchema: validationSchema,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true,
    });

    const filterUser = (email: string) => {
        dispatch({
            type: "SEARCH",
            value: {
                ...filter, email: email, pageNumber: 1
            },
        });
    }

    useEffect(() => {
        setBanner("rocky-peak-sunset.png");
    });

    const [users, , , reloadUsers] = useService(() =>
        observationsService.userByObservation(ticketNumber)
    );

    const [canViewEditObservation] = useService(() =>
        observationsService.canViewEditObservation(ticketNumber)
    );

    const [publication] = useService(() =>
        documentsService.documentIDByPublication(ticketNumber)
    );

    useEffect(() => {
        service(() => observationsService.getRemainingDays(ticketNumber), {
            callback: (result) => {
                if (result.remainingDays <= 0) {
                    setRemainingDays(0);
                    setReadOnlyRemainingDays(false);
                }
                else {
                    setRemainingDays(result.remainingDays);
                    setReadOnlyRemainingDays(true);
                }
            }
        });
    }, []);

    return (
        <>
            {loading ? (
                <SpinnerArea loading={loading || saving}>
                    <Box className={styles.observationBox}>
                        <div className={styles.observationTitle}>
                            <h2>{t("publication")} <strong>{t("publicationHUB")}</strong></h2>
                            <h3>{t("observations")}</h3>
                        </div>
                    </Box>
                </SpinnerArea>
            ) : (
                <SpinnerArea loading={loading || saving}>
                    <Box className={styles.observationBox}>
                        <div className={styles.observationTitle}>
                            <h2>{t("publication")} <strong>{t("publicationHUB")}</strong></h2>
                            <h3>{t("observations")}</h3>
                        </div>

                        {!canViewEditObservation?.canViewObservations ? (
                            <Paper className={styles.main}>
                                <h2 className={styles.publicationDontSeeThisScreen}>
                                    <ErrorOutlineIcon /> {t("dontSeeThisScreen")}
                                </h2>
                            </Paper>
                        ) : (
                            <Paper className={styles.main}>
                                <div className={styles.observationContent}>
                                    <BackButton text="back" />
                                    <div className={styles.publicationContainer}>
                                        <div className={styles.publicationNumber}>
                                            <p>
                                                <strong>{t("publication")}: </strong>
                                                {ticketNumber}
                                            </p>
                                        </div>
                                    </div>
                                    {canViewEditObservation.canEditObservation ? (
                                        <div>
                                            <p>{t("observationsAclarations1") + remainingDays + t("observationsAclarations2")}</p>
                                        </div>
                                    ) : (
                                        <></>
                                    )
                                    }
                                </div>
                                {canViewEditObservation.canEditObservation ? (
                                    <form
                                        onSubmit={formik.handleSubmit}
                                        onReset={formik.handleReset}
                                        className={styles.observationsForm}
                                    >
                                        <div className={styles.formContainer}>
                                            <div className={styles["area-date"]}>
                                                <FormControl fullWidth>
                                                    <FormLabel id="creationDate-label">
                                                        {t("date")}
                                                    </FormLabel>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            readOnly={true}
                                                            className={styles.picker}
                                                            format="MM/DD/YYYY"
                                                            defaultValue={dayjs(new Date())}
                                                            slotProps={{
                                                                textField: {
                                                                    name: "creationDate",
                                                                    id: "creationDate",
                                                                    fullWidth: true,
                                                                },
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </FormControl>
                                            </div>
                                            <div className={styles["area-document"]}>
                                                <FormControl fullWidth>
                                                    <p>{t("downloadConceptNoteDocument")}</p>
                                                    {<DocumentDownloadButton documentId={publication?.conceptNoteDocumentId} />}
                                                </FormControl>
                                            </div>
                                            {!readOnlyRemainingDays ? (
                                                <div className={styles["area-deadline-passed"]}>
                                                    <Alert severity="error" icon={false} >
                                                        {t("observationDeadlinePassed")}
                                                    </Alert>
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                            <div className={styles["area-observation"]}>
                                                <FormControl fullWidth>
                                                    <FormLabel id="description-label">
                                                        <p>
                                                            {t("observation")}
                                                            <em>(Max 2.000 characters)</em>
                                                        </p>
                                                    </FormLabel>
                                                    <TextField
                                                        id="observation"
                                                        name="observation"
                                                        disabled={!readOnlyRemainingDays}
                                                        placeholder={t("observation") || ""}
                                                        fullWidth
                                                        rows={3}
                                                        multiline
                                                        value={formik.values.observation || " "}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        helperText={formik.touched.observation && formik.errors.observation}
                                                        error={formik.touched.observation && Boolean(formik.errors.observation)}
                                                    />
                                                </FormControl>
                                            </div>
                                        </div>
                                        <div className={styles.summaryActions}>
                                            <Button
                                                disabled={!readOnlyRemainingDays}
                                                onClick={() => validateBeforeExecute(formik, () => addObservation(formik.values))}>
                                                {t("addNewObservation")}
                                            </Button>
                                        </div>
                                    </form>
                                ) : (
                                    <></>
                                )}
                                <div className={styles.observationContent}>
                                    <hr className={styles.hrObservation} />
                                    <p><strong>{t("observation")}</strong></p>
                                    <div className={styles.selectFilter}>
                                        <SelectFilter value={filter?.email} items={users ?? []} readOnly={false} disabled={false}
                                            search={(user) => filterUser(user)} label="" />
                                    </div>
                                </div>
                                <div className={styles.observationTable}>
                                    <PaginatedDataTable<AllObservationData>
                                        rows={data ? data.list : []}
                                        totalRows={data ? data.totalRows : 0}
                                        initialPage={filter.pageNumber - 1}
                                        pageSize={filter.pageSize}
                                        columns={columns}
                                        handlePageChange={handlePageChange}
                                        emptyMessage={t("noRowsMessage", {
                                            entity: t("observations"),
                                        })}
                                        loading={false}
                                        autoHeight={(data?.list.length || 0) > 0}
                                        rowAutoHeight={(data?.list.length || 0) > 0}
                                    />
                                </div>
                                <div className={styles.goToSummary}>
                                    <Button variant="contained" onClick={ () => navigate(`/summary/${ticketNumber}`)}>
                                        {t("goWorkflow")}
                                    </Button>
                                </div>
                            </Paper>
                        )}
                        {editState && (
                            <ConfirmDialog
                                open={true}
                                onClose={() => setEditState(undefined)}
                                onAccept={() => editStateObservation()}
                                text={t("editStateObservation")}
                            />
                        )}
                    </Box>
                </SpinnerArea>
            )}
        </>
    );
}

export default ObservationsByPublications;